import React from 'react';
import { useAccount } from './account/AccountProvider';
import { DropdownButton } from './DropdownButton';
import { useQuery } from 'libs/react';
import { DealStagesQuery } from 'graphql/DealStage/queries/DealStagesQuery';
import './StageSelect.scss';
/**
 * Returns DealStage options from the database only, sorted by position.
 * The `value` of each option is the stage `tag` (uppercase), matching `deal.stage`.
 */
export function useDealStageOptions() {
    var _a;
    const [data] = useQuery(DealStagesQuery, { variables: { input: {} } });
    const stages = (_a = data === null || data === void 0 ? void 0 : data.dealStages) !== null && _a !== void 0 ? _a : [];
    return stages
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((s) => {
        var _a, _b;
        return ({
            value: (_a = s.tag) !== null && _a !== void 0 ? _a : s.id,
            label: s.title,
            color: (_b = s.color) !== null && _b !== void 0 ? _b : 'blue'
        });
    });
}
/**
 * Returns the full merged list of DEAL stage options: system defaults + custom DB stages.
 * System defaults are inserted before COMPLETED; DB stages override colors of defaults.
 */
export function useMergedDealStageOptions() {
    const account = useAccount();
    const dbStages = useDealStageOptions();
    const defaults = getStageOptions('DEAL', account);
    const completedIndex = defaults.findIndex((o) => o.value === 'COMPLETED');
    const before = completedIndex >= 0 ? defaults.slice(0, completedIndex) : defaults;
    const after = completedIndex >= 0 ? defaults.slice(completedIndex) : [];
    const hardcodedValues = new Set(defaults.map((o) => o.value));
    const custom = dbStages.filter((s) => !hardcodedValues.has(s.value));
    const dbColorByTag = new Map(dbStages.filter((s) => s.color).map((s) => [s.value, s.color]));
    const merged = [...before, ...custom, ...after];
    return merged.map((o) => dbColorByTag.has(o.value) ? Object.assign(Object.assign({}, o), { color: dbColorByTag.get(o.value) }) : o);
}
export function StageSelect(props) {
    const { disabled, type, value } = Object.assign({}, props);
    const account = useAccount();
    const mergedDealStages = useMergedDealStageOptions();
    let stageOptions;
    if (type === 'DEAL') {
        stageOptions = mergedDealStages;
    }
    else {
        stageOptions = getStageOptions(type, account);
    }
    return (React.createElement("div", { className: "status-select-container" },
        React.createElement(DropdownButton, { style: "normal", size: "small", options: stageOptions, value: value, onSelect: props.onChange, disabled: disabled })));
}
export function getStageOptions(type, account) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    let stageOptions = [];
    const hasApproval = (_d = JSON.parse((_c = (_b = (_a = account === null || account === void 0 ? void 0 : account.configs) === null || _a === void 0 ? void 0 : _a.find((conf) => conf.fieldKey === 'APPROVAL_PROCESS')) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '{}')) === null || _d === void 0 ? void 0 : _d.value;
    const hasDirectSales = (_h = JSON.parse((_g = (_f = (_e = account === null || account === void 0 ? void 0 : account.configs) === null || _e === void 0 ? void 0 : _e.find((conf) => conf.fieldKey === 'HAS_DIRECT_SALES')) === null || _f === void 0 ? void 0 : _f.value) !== null && _g !== void 0 ? _g : '{}')) === null || _h === void 0 ? void 0 : _h.value;
    if (type === 'DEAL') {
        stageOptions.push({
            value: 'DRAFT',
            label: t('Draft'),
            color: 'light-gray'
        });
        stageOptions.push({
            value: 'NEW',
            label: t('New'),
            color: 'blue'
        });
        stageOptions.push({
            value: 'IN_PROCESS',
            label: t('In process'),
            color: 'blue'
        });
        stageOptions.push({
            value: 'READY_TO_SEND',
            label: t('Ready to send'),
            color: 'orange'
        });
        if (hasApproval === 'TRUE') {
            // TODO : check if account has approval process
            stageOptions.push({
                value: 'PENDING_APPROVAL',
                label: t('Pending Approval'),
                color: 'orange'
            });
            stageOptions.push({
                value: 'APPROVED',
                label: t('Approved'),
                color: 'green'
            });
        }
        if (account && account.tag === 'clubpiscine') {
            stageOptions.push({
                value: 'FOLLOW_UP_24H',
                label: t('Follow up 24h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_48H',
                label: t('Follow up 48h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_72H',
                label: t('Follow up 72h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_1_WEEK',
                label: t('Follow up 1 week'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_30_DAYS',
                label: t('Follow up 30 days'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_NEXT_SEASON',
                label: t('Follow up next season'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_1_YEAR',
                label: t('Follow up 1 year'),
                color: 'yellow'
            });
        }
        stageOptions.push({
            value: 'SENT',
            label: t('Sent'),
            color: 'green'
        });
        if (hasDirectSales === 'TRUE') {
            stageOptions.push({
                value: 'PENDING_PAYMENT',
                label: t('Pending payment'),
                color: 'orange'
            });
        }
        stageOptions.push({
            value: 'COMPLETED',
            label: t('Completed'),
            color: 'green'
        });
    }
    else if (type === 'ORDER' || type === 'TASK_LIST') {
        stageOptions = [
            {
                value: 'REVIEW',
                label: t('Review'),
                color: 'blue'
            },
            {
                value: 'PENDING',
                label: t('Pending'),
                color: 'orange'
            },
            {
                value: 'PROCESSING',
                label: t('Processing'),
                color: 'green'
            }
        ];
    }
    return stageOptions;
}
